import React from 'react'
import type { ReactNode } from 'react'
import { ImageBackground, ScrollView, StatusBar, TouchableOpacity, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useStyleSheet, StyleService } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { nutritionIntroImage, stressManagementIntroImage } from '@src/assets/images'
import { useIsDarkMode } from '@src/config/theme'

const HERO_IMAGE_ASPECT_RATIO = 393 / 390
const CLOSE_BUTTON_BACKGROUND_COLOR = 'rgba(142, 169, 156, 0.7)'

interface AppointmentOfferProps {
  data: {
    title: string
    description: string
    benefits: readonly string[]
    note?: string
    primaryActionText: string
    secondaryActionText: string
  }
  onSkipPress: () => void
  onPrimaryPress: () => void
  coachingKind: 'stress' | 'nutrition'
  onClosePress?: () => void
  primaryButtonDisabled?: boolean
  secondaryButtonDisabled?: boolean
  primaryButtonAccessoryRight?: ReactNode
}

export const AppointmentOffer = ({
  data,
  onSkipPress,
  onPrimaryPress,
  coachingKind,
  onClosePress,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  primaryButtonAccessoryRight,
}: AppointmentOfferProps) => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const isDarkMode = useIsDarkMode()

  const { primaryActionText, secondaryActionText, title, description, benefits, note } = data

  const getImageSource = () => {
    return coachingKind === 'stress'
      ? stressManagementIntroImage(isDarkMode).imageSource
      : nutritionIntroImage(isDarkMode).imageSource
  }

  return (
    <View style={styles.container}>
      <StatusBar translucent backgroundColor="transparent" barStyle="light-content" />

      {!!onClosePress && (
        <TouchableOpacity
          accessibilityLabel="Close"
          onPress={onClosePress}
          style={[
            styles.closeButton,
            { backgroundColor: CLOSE_BUTTON_BACKGROUND_COLOR, top: insets.top + 12 },
          ]}
        >
          <Icon name="x" size={16} weight="bold" style={styles.closeButtonIcon} />
        </TouchableOpacity>
      )}

      <View pointerEvents="none" style={styles.heroContainer}>
        <ImageBackground source={getImageSource()} style={styles.heroImage} resizeMode="cover">
          <View style={styles.heroOverlay} />
        </ImageBackground>
      </View>

      <ScrollView
        bounces={false}
        contentContainerStyle={[
          styles.contentContainer,
          { paddingBottom: Math.max(insets.bottom, 16) },
        ]}
      >
        <View style={styles.content}>
          <View style={[styles.topContent, { paddingTop: Math.max(insets.top + 150, 220) }]}>
            <Text type="title-1" style={styles.title}>
              {title}
            </Text>

            <Text type="large" style={styles.description}>
              {description}
            </Text>

            {benefits.length > 0 && (
              <View style={styles.benefitsContainer}>
                {benefits.map((benefit) => (
                  <View key={benefit} style={styles.benefitRow}>
                    <Icon name="check-circle" size={16} weight="fill" style={styles.benefitIcon} />
                    <Text type="large" style={styles.benefitText}>
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.footer}>
            {!!note && (
              <View style={styles.noteInfoRow}>
                <Text type="large" lineSpacing="none" style={styles.noteInfoText}>
                  {note}
                </Text>
                <Icon name="info" size={18} style={styles.noteInfoIcon} />
              </View>
            )}

            <View style={styles.buttonsContainer}>
              <Button
                type="primary"
                size="block"
                onPress={onPrimaryPress}
                accessibilityLabel={primaryActionText}
                disabled={primaryButtonDisabled}
                accessoryRight={primaryButtonAccessoryRight}
              >
                {primaryActionText}
              </Button>

              <Button
                type="transparent"
                size="block"
                onPress={onSkipPress}
                accessibilityLabel={secondaryActionText}
                disabled={secondaryButtonDisabled}
                textStyle={styles.dismissButtonText}
              >
                {secondaryActionText}
              </Button>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  heroContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 420,
  },
  heroImage: {
    width: '100%',
    aspectRatio: HERO_IMAGE_ASPECT_RATIO,
  },
  heroOverlay: {
    flex: 1,
  },
  closeButton: {
    position: 'absolute',
    left: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  closeButtonIcon: {
    color: 'theme.solid.white',
  },
  contentContainer: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
  },
  topContent: {
    paddingHorizontal: 24,
  },
  title: {
    color: 'theme.text.primary',
    maxWidth: 296,
  },
  description: {
    marginTop: 16,
    color: 'theme.text.primary',
    maxWidth: 345,
  },
  benefitsContainer: {
    marginTop: 28,
    gap: 12,
    maxWidth: 320,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  benefitIcon: {
    color: 'theme.success.base',
    marginTop: 4,
  },
  benefitText: {
    flex: 1,
    color: 'theme.text.primary',
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 16,
  },
  noteInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  noteInfoText: {
    color: 'theme.text.primary',
    textAlign: 'center',
  },
  noteInfoIcon: {
    color: 'theme.text.secondary',
  },
  buttonsContainer: {
    gap: 8,
  },
  dismissButtonText: {
    color: 'theme.text.primary',
    fontFamily: 'NBInternationalProBol',
    fontWeight: '700',
    lineHeight: 18,
  },
})
